//@flow
import React from 'react';
import { Link } from 'react-router';
import MediaQuery from 'react-responsive';

import Query from '../../Utils/query';
import CookiesData from '../../Utils/cookies-data';
import GetPromoButton from './GetPromoButton.react';
import PromoEmailCaptureModal from '../../Catalog/components/PromoEmailCaptureModal.react';
import {getImageUrl, parseBannerUiConfigStyles} from '../../Stores/Mktg/mktgStoreClass';
import UrlData from '../../Utils/url-data';

import imageSmall from '../../../images/catalog-banner/nonmem-catalog-banner-sm-md@2x.jpg';
import imageXlarge from '../../../images/catalog/catalog-banner-background-xl-lg.jpg';

import type { BannerConfig, BannerUiConfig } from '../../Stores/Mktg/mktgModelInterfaces';
import type MembershipPlanModel from '../../Stores/Membership/membershipPlanModel';
import BannerSlider from '../../../components/BannerSlider';

const HOST = UrlData.getFullHostname();

type Props = {
  bannerConfig:?BannerConfig,
  promoUnlockEnabled:boolean,
  membershipPlan:MembershipPlanModel,
};

const getFullLinkData = (path:string, external?:boolean) => {
  let url = path;
  let useAnchor = false;
  if (external || path.startsWith('http')) {
    useAnchor = true;
  }
  if (external && !path.startsWith('http')) {
    url = `${HOST}${path}`;
  }
  return {url, useAnchor};
};


const defaultBannerConfig = {
  items: [
    {
      className: 'homepage-banner default',
      title: 'Finally, feel-good snacks that love you back',
      text1: 'Discover snacks that taste amazing, without the artificial junk.',
      text2: '',
      text3: '',
      text4: '',
    },
  ],
};

type ImageSize = 'sm'|'md'|'lg'|'xl'|'xxl';
const ImageKeys = {
  'sm': 'imageSmall',
  'md': 'imageMedium',
  'lg': 'imageLarge',
  'xl': 'imageXlarge',
  'xxl': 'imageXXlarge',
};

export default function DefaultBanner(props:Props) {
  const showUnlock = _emailUncaptured(props);

  const bannerConfig = {
    ...defaultBannerConfig,
    ...(props.bannerConfig || {}),
  };

  if (bannerConfig.items.length > 1) {
    return renderItems(bannerConfig.items);
  }

  const bannerUiConfig = bannerConfig.items[0];

  if (!bannerUiConfig) return null;

  const topBanner = _getTopBanner(showUnlock, bannerUiConfig);

  let bannerContents = (
    <div className={bannerUiConfig.className}>
      <MediaQuery query={Query.toEm({ max: 440 })}>
        <div className="top-banner snack-image" style={_getStyle(bannerUiConfig, "sm")}>
          {topBanner}
        </div>
      </MediaQuery>

      <MediaQuery query={Query.toEm({ min: 441, max: 640 })}>
        <div className="top-banner snack-image" style={_getStyle(bannerUiConfig, "md")}>
          {topBanner}
        </div>
      </MediaQuery>

      <MediaQuery query={Query.toEm({ min: 641, max: 840 })}>
        <div className="top-banner snack-image" style={_getStyle(bannerUiConfig, "lg")}>
          {topBanner}
        </div>
      </MediaQuery>

      <MediaQuery query={Query.toEm({ min: 841, max: 1280 })}>
        <div className="top-banner snack-image" style={_getStyle(bannerUiConfig, "xl")}>
          {topBanner}
        </div>
      </MediaQuery>

      <MediaQuery query={Query.toEm({ min: 1281 })}>
        <div className="top-banner snack-image" style={_getStyle(bannerUiConfig, "xxl")}>
          {topBanner}
        </div>
      </MediaQuery>
    </div>
  );

  if (bannerUiConfig.link) {
    const {url, useAnchor} = getFullLinkData(bannerUiConfig.link, bannerUiConfig.isExternalLink);
    if (useAnchor) {
      bannerContents = <a href={url}>{bannerContents}</a>;
    }
    else {
      bannerContents = <Link to={url}>{bannerContents}</Link>;
    }
  }

  return (
    <div>
      <div className="banner-contents">{bannerContents}</div>
      <PromoEmailCaptureModal />
    </div>
  );
}

function _getTopBanner(showUnlock:boolean, bannerUiConfig:BannerUiConfig) {
  const tealClass = showUnlock ? " teal" : "";
  const btnClass = showUnlock ? " btn" : "";
  return (
    <div className={"text-holder" + btnClass}>
      {bannerUiConfig.title && <h1>{bannerUiConfig.title}</h1>}
      <div className="text text-line1">
        {bannerUiConfig.text1}
        <div className={"text text-line2" + tealClass}>
          {bannerUiConfig.text2}
          {_getPromoText(showUnlock, bannerUiConfig)}
        </div>
        {_getPromoBtn(showUnlock, bannerUiConfig)}
      </div>
    </div>
  );
}

function _getStyle(bannerUiConfig:BannerUiConfig, imageSize:ImageSize):Object {
  if (!bannerUiConfig) {return {};}
  const styles = parseBannerUiConfigStyles(bannerUiConfig.styles);
  // If xxl image not available, use xl image
  const image = imageSize === 'xxl'
    ? (bannerUiConfig[ImageKeys['xxl']] || bannerUiConfig[ImageKeys['xl']])
    : bannerUiConfig[ImageKeys[imageSize]];

  if (image) {
    return { backgroundImage: `url("${getImageUrl(image)}")` };
  }
  // If we don't have an image specified, check if
  // styles.hero is defined and use that. If not,
  // use default images
  if (styles && styles.hero) {
    return styles.hero;
  }
  const defaultImage = imageSize === 'sm'
    ? imageSmall
    : imageXlarge;
  return { backgroundImage: `url("${defaultImage}")` };
}

function _emailUncaptured(props:Props) {
  return props.promoUnlockEnabled && !CookiesData.getEmail();
}

function _getPromoText(uncaptured:boolean, bannerUiConfig:BannerUiConfig) {
  if (uncaptured) return null;
  return <span className="text-line3">{bannerUiConfig.text3}</span>;
}

function _getPromoBtn(uncaptured:boolean, bannerUiConfig:BannerUiConfig) {
  if (uncaptured) {
    return <GetPromoButton />;
  }
  let style = {};
  const styles = parseBannerUiConfigStyles(bannerUiConfig ? bannerUiConfig.styles : '');
  if (styles['.text-terms']) {
    style = styles['.text-terms'];
  }
  return <div className="text-terms text-4" style={style}>{bannerUiConfig.text4}</div>;
}

function renderItems(items:BannerUiConfig[]) {
  return (
    <div className="banner-slider-container">
      <BannerSlider
        items={items}
      />
    </div>
  );
}
